import React from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import * as Yup from 'yup';
export default function ProductionModelDialog({
  open,
  onClose,
  mode = 'production',
  titleNew = 'New',
  titleEdit = 'Edit',
  initialValues,
  validationSchema,
  onSubmit,
  orderOptions = [],
  statusOptions = [],
  shifts = [],
  findOrderById,
}) {
  const isProduction = mode === 'production';
  const fieldBoxSx = { width: { xs: '100%', sm: 'calc(50% - 12px)' } };

  const ReusableTextField = ({ id, name, label, value, onChange, onBlur, touched, error, type = 'text', readOnly = false, disabled = false, placeholder }) => (
    <TextField
      fullWidth
      size="small"
      id={id}
      name={name}
      label={label}
      value={value ?? ''}
      onChange={onChange}
      onBlur={onBlur}
      error={Boolean(touched) && Boolean(error)}
      helperText={Boolean(touched) && error}
      type={type}
      InputLabelProps={(type === 'date' || type === 'time') ? { shrink: true } : undefined}
      inputProps={placeholder ? { placeholder } : undefined}
      InputProps={{ readOnly }}
      disabled={disabled}
      sx={{
        m: 0,
        '& .MuiOutlinedInput-root': { borderRadius: '12px' },
        '& .MuiInputBase-input': { fontSize: 14 },
        '& .MuiInputLabel-root': { fontSize: 14 }
      }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '500px',
          maxWidth: '500px',
          m: '50px auto',
          height: 'auto',
          maxHeight: 'calc(100vh - 100px)',
          display: 'flex',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>{initialValues?.order_id ? titleEdit : titleNew}</DialogTitle>
      <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 1, pb: 1, px: 2, flex: 1, overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                {/* Production fields - new schema */}
                {isProduction && (
                  <>
                    {values.order_id ? (
                      <Box sx={fieldBoxSx}>
                        <ReusableTextField
                          id="order_id"
                          name="order_id"
                          label="Order ID"
                          type="number"
                          value={values.order_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          touched={touched.order_id}
                          error={errors.order_id}
                          disabled={Boolean(values.order_id)} />
                      </Box>
                    ) : null}


                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="customer_name"
                        name="customer_name"
                        label="Customer Name"
                        value={values.customer_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.customer_name}
                        error={errors.customer_name}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="paper_grade"
                        name="paper_grade"
                        label="Paper Grade"
                        value={values.paper_grade}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.paper_grade}
                        error={errors.paper_grade}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="gsm"
                        name="gsm"
                        label="GSM"
                        type="number"
                        value={values.gsm}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.gsm}
                        error={errors.gsm}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="width"
                        name="width"
                        label="Width (mm)"
                        type="number"
                        value={values.width}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.width}
                        error={errors.width}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="diameter"
                        name="diameter"
                        label="Diameter (mm)"
                        type="number"
                        value={values.diameter}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.diameter}
                        error={errors.diameter}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="total_quantity_tons"
                        name="total_quantity_tons"
                        label="Total Quantity (Tons)"
                        type="number"
                        value={values.total_quantity_tons}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.total_quantity_tons}
                        error={errors.total_quantity_tons}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="dispatch_value"
                        name="dispatch_value"
                        label="Dispatch Value"
                        type="number"
                        value={values.dispatch_value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.dispatch_value}
                        error={errors.dispatch_value}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="additional_notes"
                        name="additional_notes"
                        label="Additional Notes"
                        value={values.additional_notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.additional_notes}
                        error={errors.additional_notes}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="order_date"
                        name="order_date"
                        label="Order Date"
                        type="date"
                        value={values.order_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.order_date}
                        error={errors.order_date}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="target_completion_date"
                        name="target_completion_date"
                        label="Target Completion Date"
                        type="date"
                        value={values.target_completion_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.target_completion_date}
                        error={errors.target_completion_date}
                      />
                    </Box>

                    <Box sx={fieldBoxSx}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          m: 0,
                          '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                          '& .MuiInputBase-input': { fontSize: 14 },
                          '& .MuiInputLabel-root': { fontSize: 14 }
                        }}
                      >
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select labelId="status-label" id="status" name="status" label="Status" value={values.status} onChange={handleChange} onBlur={handleBlur}>
                          {statusOptions.map(s => <MenuItem sx={{ fontSize: '14px' }} key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                  </>
                )}

                {/* Dispatch fields - new schema */}
                
                {!isProduction && (
                  <>
                    {/* order_id */}
                    {/* {values.order_id ? ( */}
                    <Box sx={fieldBoxSx}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          m: 0,
                          '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                          '& .MuiInputBase-input': { fontSize: 14 },
                          '& .MuiInputLabel-root': { fontSize: 14 }
                        }}
                      >
                        <InputLabel id="order-id-label">Order</InputLabel>
                        <Select
                          labelId="order-id-label"
                          id="order_id"
                          name="order_id"
                          value={values.order_id}
                          label="Order"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.order_id && Boolean(errors.order_id)}
                          disabled={!isProduction && Boolean(initialValues?.dispatch_id)}
                        >
                          {orderOptions.map(opt => (
                            <MenuItem sx={{ fontSize: '14px' }} key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {/* ) : null} */}

                    {/* dispatch_quantity_tons */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="dispatch_quantity_tons"
                        name="dispatch_quantity_tons"
                        label="Dispatch Qty (Tons)"
                        type="number"
                        value={values.dispatch_quantity_tons}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.dispatch_quantity_tons}
                        error={errors.dispatch_quantity_tons}
                      />
                    </Box>

                    {/* dispatch_date */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="dispatch_date"
                        name="dispatch_date"
                        label="Dispatch Date"
                        type="date"
                        value={values.dispatch_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.dispatch_date}
                        error={errors.dispatch_date}
                      />
                    </Box>

                    {/* dispatch_time (optional) */}
                    {/* <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="dispatch_time"
                        name="dispatch_time"
                        label="Dispatch Time"
                        type="time"
                        placeholder="HH:MM"
                        value={values.dispatch_time}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.dispatch_time}
                        error={errors.dispatch_time}
                      />
                    </Box> */}

                    {/* arrival_time (optional) */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="arrival_time"
                        name="arrival_time"
                        label="Arrival Time"
                        type="time"
                        placeholder="HH:MM"
                        value={values.arrival_time}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.arrival_time}
                        error={errors.arrival_time}
                      />
                    </Box>

                    {/* trailer_information */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="trailer_information"
                        name="trailer_information"
                        label="Trailer Information"
                        value={values.trailer_information}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.trailer_information}
                        error={errors.trailer_information}
                      />
                    </Box>

                    {/* transport_type */}
                    <Box sx={fieldBoxSx}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          m: 0,
                          '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                          '& .MuiInputBase-input': { fontSize: 14 },
                          '& .MuiInputLabel-root': { fontSize: 14 }
                        }}
                      >
                        <InputLabel id="transport-type-label">Transport Type</InputLabel>
                        <Select labelId="transport-type-label" id="transport_type" name="transport_type" label="Transport Type" value={values.transport_type} onChange={handleChange} onBlur={handleBlur} error={touched.transport_type && Boolean(errors.transport_type)}>
                          <MenuItem sx={{ fontSize: '14px' }} value="Full Load">Full Load</MenuItem>
                          <MenuItem sx={{ fontSize: '14px' }} value="Partial Load">Partial Load</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* route_details */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="route_details"
                        name="route_details"
                        label="Route Details"
                        value={values.route_details}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.route_details}
                        error={errors.route_details}
                      />
                    </Box>

                    {/* transport_company (optional) */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="transport_company"
                        name="transport_company"
                        label="Transport Company"
                        value={values.transport_company}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.transport_company}
                        error={errors.transport_company}
                      />
                    </Box>

                    {/* client_name */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="client_name"
                        name="client_name"
                        label="Client Name"
                        value={values.client_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.client_name}
                        error={errors.client_name}
                      />
                    </Box>

                    {/* dispatch_notes (optional) */}
                    <Box sx={fieldBoxSx}>
                      <ReusableTextField
                        id="dispatch_notes"
                        name="dispatch_notes"
                        label="Dispatch Notes"
                        value={values.dispatch_notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.dispatch_notes}
                        error={errors.dispatch_notes}
                      />
                    </Box>

                    {/* status */}
                    <Box sx={fieldBoxSx}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          m: 0,
                          '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                          '& .MuiInputBase-input': { fontSize: 14 },
                          '& .MuiInputLabel-root': { fontSize: 14 }
                        }}
                      >
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select labelId="status-label" id="status" name="status" label="Status" value={values.status} onChange={handleChange} onBlur={handleBlur}>
                          {statusOptions.map(s => <MenuItem sx={{ fontSize: '14px' }} key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ py: 1.25 }}>
              <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button type="submit" variant="contained" sx={{ textTransform: 'none' }}>{initialValues?.order_id ? 'Update' : 'Create'}</Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
